import mixpanel from 'mixpanel-browser';

import {
    FQ_NODE_ENV,
    MIXPANEL_TOKEN_DEV,
    MIXPANEL_TOKEN_PRODUCTION,
    MIXPANEL_TOKEN_STAGE,
} from '@/utils/constants/common';

const EXCLUDED_ORGANIZATION_IDS = ['d34afa29-9c29-4e64-851b-3eea12fbf93a'];

const selectEnvironment = () => {
    if (FQ_NODE_ENV === 'development') return MIXPANEL_TOKEN_DEV;
    if (FQ_NODE_ENV === 'stage') return MIXPANEL_TOKEN_STAGE;
    if (FQ_NODE_ENV === 'production') return MIXPANEL_TOKEN_PRODUCTION;

    return '';
};

export const initializeMixpanel = () => {
    const mixPanelInit = () =>
        mixpanel.init(selectEnvironment(), {
            debug: FQ_NODE_ENV !== 'production',
            // Route data to Mixpanel's EU servers for data residency compliance
            api_host: 'https://api-eu.mixpanel.com',
        });
    environmentGuard(mixPanelInit);
};

export type MixPanelProps = Record<string, string | number | boolean | undefined | null>;

const environmentGuard = (callback: () => void) => {
    if (!FQ_NODE_ENV) {
        return;
    }

    callback();
};

export const initMixpanelProps = ({ userId, organizationId }) => {
    const callMixPanelActions = () => {
        if (EXCLUDED_ORGANIZATION_IDS.includes(organizationId)) {
            mixpanel.opt_out_tracking();
            console.warn('Disabled tracking for organization:', organizationId);
            return;
        }

        mixpanel.identify(userId);
        mixpanel.register_once({ organizationId });
    };

    environmentGuard(callMixPanelActions);
};

export const resetMixpanel = () => {
    const callMixpanelReset = () => {
        try {
            mixpanel.reset();
        } catch (err) {
            console.error(err);
        }
    };

    environmentGuard(callMixpanelReset);
};

export function mixpanelTracking(mixpanelAction: MIXPANEL_ACTIONS, props?: MixPanelProps) {
    const trackMixPanel = () => mixpanel.track(mixpanelAction, props);
    environmentGuard(trackMixPanel);
}

export enum MIXPANEL_ACTIONS {
    OPEN_APP = 'OpenApp',
    LOGIN = 'Login',
    START_REGISTER_ORGANIZATION = 'StartRegisterOrganization',
    REGISTER_ORGANIZATION_SUCCESS = 'RegisterOrganizationSuccess',
    REGISTER_ORGANIZATION_FAILED = 'RegisterOrganizationFailed',
    NEW_REQUEST = 'NewRequest',
    APPROVED_REQUEST = 'ApprovedRequest',
    REJECTED_REQUEST = 'RejectedRequest',
    APPROVED_DELETION_REQUEST = 'ApprovedDeletionRequest',
    REJECTED_DELETION_REQUEST = 'RejectedDeletionRequest',
    DELETED_REQUEST = 'DeletedRequest',
    PAYMENT_REDIRECTION_FAILED = 'PaymentRedirectionFailed',
    PAYMENT_CANCELED = 'PaymentCanceled',
    LAST_EMPLOYEE_INVITED = 'LastEmployeeInvited',
    TRY_UNBLOCKING_EMPLOYEE_OVER_LIMIT_EXCEEDED = 'TryUnblockingEmployeeOverLimit',
    MY_SUBSCRIPTIONS_CLICK_ON_UPGRADE_BANNER = 'MySubscriptionsClickOnUpgradeBanner',
    MY_SUBSCRIPTIONS_CLICK_ON_UPGRADE_MODAL = 'MySubscriptionsClickOnUpgradeModal',
    MY_SUBSCRIPTIONS_CLICK_AFTER_LAST_INVITE = 'MySubscriptionsClickAfterLastInvite',
    FIRST_PAYMENT_SUCCESS = 'FirstPaymentSuccess',
    LANDINGPAGE_PAYMENT_FAILED = 'LandingPagePaymentFailed',
    EMPLOYEE_LIST_VIEWED = 'EmployeeListViewed',
    EMPLOYEE_BLOCKED = 'EmployeeBlocked',
    EMPLOYEE_UNBLOCKED = 'EmployeeUnblocked',
    INVITATION_TAB_VIEWED = 'InvitationTabViewed',
    NEW_EMPLOYEE_INVITED = 'NewEmployeeInvited',
    NEW_EMPLOYEE_ACCOUNT_ACTIVATED = 'NewEmployeeAccountActivated',
    DELETE_ORGANIZATION_SUCCESSFUL = 'DeleteOrganisationSuccessful',
    REQUEST_TYPE_ADDED = 'RequestTypeAdded',
    REQUEST_TYPE_EDITED = 'RequestTypeEdited',
    REQUEST_TYPE_REMOVED = 'RequestTypeRemoved',
    REQUEST_TYPE_PERSONALIZED = 'RequestTypePersonalized',
    PERSONALIZE_REQUEST_VIEWED = 'PersonalizeRequestViewed',
    NEW_DAY_OFF_ADDED = 'NewDayOffAdded',
    HOLIDAYS_IMPORT_CLICKED = 'HolidaysImportClicked',
    HOLIDAYS_IMPORT_SUCCESSFUL = 'HolidaysImportSuccessful',
    USER_PROFILE_VIEWED = 'UserProfileViewed',
    SELF_PROFILE_VIEWED = 'SelfProfileViewed',
    USER_PERMISSION_CHANGED = 'UserPermissionChanged',
    APPROVER_ADDED_TO_USER = 'ApproverAddedToUser',
    APPROVER_REMOVED_FROM_USER = 'ApproverRemovedFromUser',
    REPORTS_EXPORTED = 'ReportExported',
    REPORTS_TAB_VIEWED = 'ReportsTabViewed',
    REPORTS_DETAILS_FOR_USER_VIEWED = 'ReportsDetailsForUserViewed',
    ONBOARDING_MODAL = 'OnboardingModal',
    ONBOARDING_MODAL_CTA = 'OnboardingModalCTA',
    ONBOARDING_STEP_1 = 'OnboardingStep1',
    ONBOARDING_STEP_2 = 'OnboardingStep2',
    ONBOARDING_STEP_3 = 'OnboardingStep3',
    ONBOARDING_STEP_4_FORM = 'OnboardingStep4_form',
    ONBOARDING_STEP_4_TEXT = 'OnboardingStep4_text',
    ONBOARDING_STEP_5 = 'OnboardingStep5',
    ONBOARDING_STEP_1_ERROR = 'OnboardingStep1Error',
    ONBOARDING_STEP_2_ERROR = 'OnboardingStep2Error',
    ONBOARDING_STEP_3_ERROR = 'OnboardingStep3Error',
    ONBOARDING_STEP_4_ERROR = 'OnboardingStep4Error',
    OPEN_SCHEDULE_TAB = 'OpenScheduleTab',
    SCHEDULE_SUBSCRIPTION_BUTTON = 'ScheduleSubscriptionButton',
    SCHEDULE_BANNER_SUBSCRIPTION_BUTTON = 'ScheduleBannerSubscriptionButton',
    SCHEDULE_TYPE_FILTER = 'ScheduleTypeFilter',
    SCHEDULE_REQUEST_DETAILS = 'ScheduleRequestDetails',
    SCHEDULE_SEARCH_BAR = 'ScheduleSearchBar',
    SCHEDULE_VIEW_WEEKLY = 'ScheduleViewWeekly',
    SCHEDULE_VIEW_MONTHLY = 'ScheduleViewMonthly',
    TEAM_CREATED = 'TeamCreated',
    DASHBOARD_VIEWED = 'DashboardViewed',
    ADMIN_PANEL_CLICKED = 'AdminPanelClicked',
    REQUEST_TYPES_VIEWED = 'RequestTypesViewed',
    PERSONALIZE_REQUEST_BUTTON_CLICKED = 'PersonalizeRequestButtonClicked',
    BACK_TO_REQUEST_TYPES = 'BackToRequestTypes',
    SCHEDULE_NEW_REQUEST_OPENED = 'ScheduleNewRequestOpened',
    SCHEDULE_NEW_REQUEST_ADDED = 'ScheduleNewRequestAdded',
    SELECT_EMPLOYEE_NEW_REQUEST_ON_BEHALF = 'SelectEmployee_NewRequestonbehalf',
    DATE_RANGE_NEW_REQUEST = 'DateRange_NewRequest',
    REQUESTS_TAB_VIEWED = 'RequestsTabViewed',
    DELETION_REQUEST_APPROVED_SUCCESSFULLY = 'DeletionRequestApprovedSuccessfully',
    NEW_REQUEST_OPENED = 'NewRequestOpened',
    COWORKERS_WORKING_CLICKED = 'CoworkersWorkingClicked',
    COWORKERS_NOT_WORKING_CLICKED = 'CoworkersNotWorkingClicked',
    COWORKERS_TAB_VIEWED = 'CoworkersTabViewed',
    SSO_LOGIN_GOOGLE = 'SSOLoginGoogle',
    SSO_LOGIN_APPLE = 'SSOLoginApple',
    SSO_REGISTER_GOOGLE = 'SSORegisterGoogle',
    SSO_REGISTER_APPLE = 'SSORegisterApple',
    MANAGE_BUTTON_SUBSCRIPTIONS = 'ManageButtonSubscriptions',
    REGISTER_GO_TO_PAYMENT = 'Register_GoToPayment',
    RESET_PASSWORD_BUTTON = 'ResetPasswordButton',
    REGISTER_WITH_GOOGLE = 'RegisterWithGoogle',
    REGISTER_WITH_GOOGLE_FROM_LOGIN = 'RegisterWithGoogleFromLogin',
    REGISTER_WITH_GOOGLE_SUCCESS = 'RegisterWithGoogleSuccess',
    REGISTER_WITH_GOOGLE_FAILED = 'RegisterWithGoogleFailed',
    REGISTER_WITH_APPLE = 'RegisterWithApple',
    REGISTER_WITH_APPLE_FROM_LOGIN = 'RegisterWithAppleFromLogin',
    REGISTER_WITH_APPLE_SUCCESS = 'RegisterWithAppleSuccess',
    REGISTER_WITH_APPLE_FAILED = 'RegisterWithAppleFailed',
    REGISTER_WITH_EMAIL = 'RegisterWithEmail',
    REGISTER_WITH_EMAIL_SUCCESS = 'RegisterWithEmailSuccess',
    REGISTER_WITH_EMAIL_FAILED = 'RegisterWithEmailFailed',
    REGISTER_COMPANY_NAME = 'RegisterCompanyName',
    REGISTER_PLAN_SELECTED = 'RegisterPlanSelected',
    REGISTER_INVOICE_DETAILS = 'RegisterInvoiceDetails',
    REGISTER_INDUSTRY_SELECTED = 'RegisterIndustrySelected',
    REGISTER_INDUSTRY_SKIPPED = 'RegisterIndustrySkipped',
    REGISTER_COUNTRY_SELECTED = 'RegisterCountrySelected',
    REGISTER_COUNTRY_SKIPPED = 'RegisterCountrySkipped',
    REGISTER_SOURCE = 'RegisterSource',
}
